import type * as MuiIcons from '@ori-ui/icons';
import type { MenuItemTypeMap } from '@ori-ui/mui';
import { GlobalCustomersDocumentApprovalDto, GlobalCustomersDocumentInfoDto } from '@ori/global-api-client-react';
import type { ReactNode } from 'react';

import type { TranslationKey } from '../models';

interface ILocalizationKey {
  id: TranslationKey;
  defaultMessage: string;
}

type LocalizationFunction = (...args: string[]) => ILocalizationKey;

interface IFormattedMessageProps {
  id: TranslationKey;
  description?: string;
  defaultMessage?: string;
  values?: { [key: string]: Date | ReactNode | number | string };
  children?: (...formattedMessage: Array<ReactNode | string>) => ReactNode;
}

interface IContentKey {
  id: string;
  value: string;
}

interface IAddressNodesFetchParams {
  searchText?: string;
  country?: string;
  top?: number;
  pathKey?: string;
}

interface Benefit {
  icon: IContentKey;
  defaultIcon: keyof typeof MuiIcons;
  title: ILocalizationKey;
  description: ILocalizationKey;
}

interface IErrorBase {
  AttemptedValue?: string;
  Code?: string;
  Message?: string;
  PropertyName?: string;
}

interface IErrors {
  Errors?: IErrorBase[];
}

type SponsorOrigin = 'ManualInput' | 'Pbs' | 'ReferralLink' | 'SponsorLocator';

interface IAddressSuggestionListItem {
  addressSummary?: string;
  description?: string;
  count: number;
  pathKey?: string;
  addressNodeId?: string;
}

interface ISuggestionProps {
  itemProps: MenuItemTypeMap<{ button: false }>;
  suggestion: IAddressSuggestionListItem;
}

interface IAddressAutocompleteProps {
  minSearchLength?: number;
  isEditable?: boolean;
  tooltipText?: ReactNode;
  searchedFields?: string[];
}

interface IConfirmDialogProps {
  handleCancel: () => void;
  handleConfirm: () => void;
  opened: boolean;
  children?: ReactNode;
}

interface IDocumentsInfo {
  approval?: GlobalCustomersDocumentApprovalDto;
  documentId: number;
  fileName: string;
  fileSize: number;
}

interface IDocumentItemProps {
  document: IDocumentsInfo;
  uploadProgress?: number;
  errorMessage?: ReactNode;
  canDelete?: boolean;
  isShowApprovalState?: boolean;
  onDocumentRemove?: (documentId: number) => void;
  isDeleteConfirmationRequired: boolean;
}

interface IThumb {
  [key: number]: string;
}

interface IDetailImage {
  [key: number]: string;
}

interface IDocumentsInfo2 {
  approval?: GlobalCustomersDocumentApprovalDto;
  documentId: number;
  fileName?: string;
  fileSize: number;
  documentTypeId: number;
  customerId?: number;
  isActive?: boolean;
  isTemporary: boolean;
  createdDate: Date;
}

interface IDocumentsInfoStoreData extends GlobalCustomersDocumentInfoDto {
  approval?: GlobalCustomersDocumentApprovalDto;
}

export type IRegistrationType = 'Consultant' | 'VipCustomer';

export enum MessageType {
  error = 'error',
  initial = 'initial',
  success = 'success',
  warning = 'warning',
}

export type {
  ILocalizationKey,
  LocalizationFunction,
  IFormattedMessageProps,
  IContentKey,
  Benefit,
  IAddressNodesFetchParams,
  IErrors,
  ISuggestionProps,
  IAddressSuggestionListItem,
  IAddressAutocompleteProps,
  SponsorOrigin,
  IConfirmDialogProps,
  IDocumentsInfo,
  IDocumentItemProps,
  IThumb,
  IDetailImage,
  IDocumentsInfo2,
  IDocumentsInfoStoreData,
};
